import { memo, ReactElement } from 'react';
import {
  DetailPaneConfigDto,
  DetailPaneDto,
  TableColumnConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  applyFormatters,
  DetailPaneCellRendererDecorator,
  DetailPaneProps,
} from '@asset-metrix/portal-ui';
import { ICellRendererParams } from 'ag-grid-community';
import { CellRendererSelectorResult } from 'ag-grid-community';

import i18n from '../../i18n/config';

import { formattersByType } from './configToColDef';

export type TableColumnConfigWithDetailPane = TableColumnConfigDto & {
  detailPaneConfig: DetailPaneConfigDto;
};

export function isTableColumnConfigWithDetailPane(
  c: TableColumnConfigDto
): c is TableColumnConfigWithDetailPane {
  return c.detailPaneConfig != null;
}

function getDetailPaneConfig(
  detailPaneConfigId: string,
  { detailPaneConfig }: TableColumnConfigWithDetailPane
): DetailPaneDto {
  const detailPane = detailPaneConfig.detailPanes.find(
    (v) => v.id === detailPaneConfigId
  );

  if (detailPane == null) {
    throw new Error(
      `Did not find config for detail pane (id: ${detailPaneConfigId}`
    );
  }

  return detailPane;
}

function buildHeader(value: string, detailPane: DetailPaneConfigDto): string {
  const prefix =
    detailPane.headlinePrefix != null ? `${detailPane.headlinePrefix} ` : '';
  const suffix =
    detailPane.headlineSuffix != null ? ` ${detailPane.headlineSuffix}` : '';

  return `${prefix}${value}${suffix}`;
}

export function getDetailPaneCellRendererDecorator(
  detailPaneConfigId: string,
  params: ICellRendererParams<Record<string, ReactElement>>,
  config: TableColumnConfigWithDetailPane,
  columnsConfig: TableColumnConfigDto[],
  cellRendererConfig?: CellRendererSelectorResult
) {
  const detailPane = getDetailPaneConfig(detailPaneConfigId, config);

  const tableData: DetailPaneProps['tableData'] = [];

  for (const detailPaneColId of detailPane.columnIds) {
    const config = columnsConfig.find((col) => col.id === detailPaneColId);
    if (config && params.data != null && config.key in params.data) {
      const newValue = applyFormatters(
        formattersByType[config.type] ?? [],
        params.data[config.key],
        config.type === 'text'
          ? {
              trueLabel: i18n.t('app.label.true'),
              falseLabel: i18n.t('app.label.false'),
              ...config,
            }
          : config
      );
      tableData.push([config.label, newValue]);
    }
  }

  return {
    component: memo(DetailPaneCellRendererDecorator),
    params: {
      headline: buildHeader(
        params.valueFormatted ?? params.value,
        config.detailPaneConfig
      ),
      cellRendererConfig,
      tableData,
    },
  };
}
